import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Form, Input, Button, Radio, Cascader, Select, Space, InputNumber, message, Spin, DatePicker } from 'antd'
import { CommonAutoComplete, CommonSelector, CommonCascador } from '@components'
import { searchCustomer } from 'src/components/CommonAutoComplete/configs'
import { areaTree } from '@configs/area'
import { leadTags, industryList } from 'src/components/CommonSelector/configs'
import { leadIntentionLevel, industryProductsOnlyValid } from 'src/components/CommonCascador/configs'
import { transferToDianShiFormOptions } from '@pages/customer/detail/config'
import { customerTypeMap } from '@configs/map'
import { addSDRIntentionLead, getSDRIntentionLeadDetail } from '@services/lead'
import { queryCustomerSalesInfoByName } from '@services/customer'
import { CustomerSalesInfo } from '@types'
import { debounce } from 'lodash'
import { isEmptyWithCustom, findProvinceAndCityValues, getArea } from '@library'
import moment from 'moment'

// eslint-disable-next-line no-useless-escape
const phoneWithMaskPatten = /^((0\d{2,3}\-\d{7,8})|(1[3-9]\d{9})|(0\d{2,3}\-\d{3}\*{4}\d{1})|(1\d{2}\*{4}\d{4}))$/

export default function SingleAdd ({ id, onCancel, onOk }: { id?: number; onCancel?: () => void; onOk?: () => void}) {
  const [form] = Form.useForm()
  const customerName = Form.useWatch('customerName', form)
  const [customerInfo, setCustomerInfo] = useState<CustomerSalesInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [initFetchCustomerInfo, setInitFetchCustomerInfo] = useState(false)
  const [initCustomerInfo, setInitCustomerInfo] = useState<CustomerSalesInfo | null>(null)
  const [dataLoading, setDataLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  // 跟踪编辑模式下是否是第一次渲染
  const isFirstRenderInEditMode = useRef(!!id)
  // 初始化数据 - 编辑模式时获取详情
  const initializeData = useCallback(async () => {
    if (!id) return

    setDataLoading(true)
    setIsEditMode(true)
    try {
      const [leadDataRes, leadErr] = await getSDRIntentionLeadDetail({ intentionLeadId: id })
      const leadData = leadDataRes?.info
      if (leadErr) {
        message.error(`获取线索详情失败(${leadErr.message})`)
        return
      }

      const area = getArea(leadData?.city, leadData?.province)?.split('-')
      const intention = leadData?.intentionLevel && leadData?.intentionSecondLevel
        ? [leadData.intentionLevel, leadData.intentionSecondLevel]
        : undefined
      const needProduct = leadData?.needProduct1 && leadData?.needProduct2
        ? [leadData.needProduct1, leadData.needProduct2]
        : undefined

      form.setFieldsValue({
        leadSource: leadData?.leadSource,
        cid: leadData?.cid,
        customerName: leadData?.customerName,
        salesChannel: leadData?.salesChannel,
        nameSales: leadData?.nameSales,
        customerType: leadData?.customerType,
        district: leadData?.district,
        area,
        firstIncomeIndustryId: leadData?.firstIncomeIndustryId,
        intention,
        needProduct,
        leadTags: leadData?.leadTagCode,
        expectBuyDate: leadData?.expectBuyDate ? moment(leadData.expectBuyDate) : undefined,
        registeredCapital: leadData?.registeredCapital,
        memberScale: leadData?.memberScale,
        monthConsumption: leadData?.monthConsumption,
        contactName: leadData?.contactName,
        contactPosition: leadData?.contactPosition,
        contactPhone: leadData?.contactPhone,
        position: leadData?.position,
        businessDemand: leadData?.businessDemand,
        cloudVendorUse: leadData?.cloudVendorUse,
      })
    } finally {
      setDataLoading(false)
    }
  }, [id, form])

  const fetchCustomerInfo = useCallback(
    debounce(async (name) => {
      if (!name) return
      // 编辑模式下，如果是第一次渲染则不调用
      if (isEditMode && isFirstRenderInEditMode.current) return
      setLoading(true)
      try {
        const params = { name }
        const [data, err] = await queryCustomerSalesInfoByName(params)
        if (!initFetchCustomerInfo) {
          setInitCustomerInfo(data)
          setInitFetchCustomerInfo(true)
        }
        if (err) {
          message.error(`获取客户信息失败(${err.message})`)
          setCustomerInfo(initCustomerInfo)
          const area = findProvinceAndCityValues(areaTree, initCustomerInfo?.province, initCustomerInfo?.city)
          form.setFieldsValue({
            cid: undefined,
            salesChannel: undefined,
            nameSales: undefined,
            area,
            firstIncomeIndustryId: initCustomerInfo?.incomeIndustry?.firstIncomeIndustry?.id,
            registeredCapital: initCustomerInfo?.registeredCapital,
          })
          return
        }
        setCustomerInfo(data)
        const area = findProvinceAndCityValues(areaTree, data?.province, data?.city)
        form.setFieldsValue({
          cid: data?.cid,
          salesChannel: data?.salesChannel,
          nameSales: data?.nameSales,
          area,
          firstIncomeIndustryId: data?.incomeIndustry?.firstIncomeIndustry?.id,
          registeredCapital: data?.registeredCapital,
        })
      } finally {
        setLoading(false)
      }
    }, 1000),
    [form, initFetchCustomerInfo, initCustomerInfo, isEditMode],
  )

  // 初始化数据
  useEffect(() => {
    initializeData()
  }, [initializeData])

  useEffect(() => {
    // 创建模式下直接调用
    if (!id) {
      fetchCustomerInfo(customerName)
    } else if (id && !isFirstRenderInEditMode.current) {
      fetchCustomerInfo(customerName)
    }

    // 如果是编辑模式的第一次渲染，将标记设为 false
    if (id && isFirstRenderInEditMode.current) {
      isFirstRenderInEditMode.current = false
    }

    return () => {
      fetchCustomerInfo.cancel()
    }
  }, [customerName, fetchCustomerInfo, id])

  const onFinish = async () => {
    const values = form.getFieldsValue()
    const payload = {
      ...values,
      expectBuyDate: values?.expectBuyDate ? moment(values?.expectBuyDate)?.format('YYYY-MM-DD') : undefined,
      province: values?.area?.[0],
      city: values?.area?.[1],
      intentionLevel: values?.intention?.[0],
      intentionSecondLevel: values?.intention?.[1],
      needProduct1: values?.needProduct?.[0],
      needProduct2: values?.needProduct?.[1],
      leadSource: isEditMode ? values.leadSource : 0,
      salesChannel: values?.salesChannel,
    }

    if (isEditMode && id) {
      // 编辑模式
      payload.leadId = id
      // 编辑模式下，由于拿不到明文的电话号，只能拿到带掩码的，故如果判断带掩码，就不更新电话号
      if (payload.contactPhone.includes('*')) {
        payload.contactPhone = undefined
      }
      const [, err] = await addSDRIntentionLead(payload)
      if (err) {
        message.error(`更新失败(${err.message})`)
        return
      }
      message.success('更新成功')
    } else {
      // 创建模式
      const [, err] = await addSDRIntentionLead(payload)
      if (err) {
        message.error(`保存失败(${err.message})`)
        return
      }
      message.success('保存成功')
    }
    onOk && onOk()
  }

  return (
    <Spin spinning={dataLoading}>
      <Form
        scrollToFirstError
        autoComplete="off"
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        onFinish={onFinish}
      >
        <Form.Item label="线索来源" name="leadSource" initialValue={0} rules={[{ required: true, message: '不能为空' }]}>
          <Radio.Group buttonStyle="solid" value={0}>
            <Radio.Button value={0} defaultChecked>企微孵化</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="CID" name="cid" dependencies={['customerName']}>
          {loading ? <Spin size="small" /> : <Input readOnly bordered={false} />}
        </Form.Item>
        <Form.Item label="客户名称" name="customerName" rules={[{ required: true, message: '不能为空' }]}>
          <CommonAutoComplete {...searchCustomer} />
        </Form.Item>
        <Form.Item label="销售通路" name="salesChannel" dependencies={['customerName']} getValueProps={(value) => ({
          value: customerInfo?.salesChannelName || value,
        })} normalize={() => customerInfo?.salesChannel}>
          {loading ? <Spin size="small" /> : <Input readOnly bordered={false} />}
        </Form.Item>
        <Form.Item label="name商务" name="nameSales" dependencies={['customerName']}>
          {loading ? <Spin size="small" /> : <Input readOnly bordered={false} />}
        </Form.Item>
        <Form.Item label="客户类型" initialValue={1} name="customerType" rules={[{ required: true, message: '不能为空' }]}>
          <Radio.Group buttonStyle="solid">
            {Object.entries(customerTypeMap).slice(0, 2).map(([value, text]) => {
              const temp = +value
              const val = isNaN(temp) ? value : temp
              return <Radio.Button key={value} value={val}>{text}</Radio.Button>
            })}
          </Radio.Group>
        </Form.Item>
        <Form.Item label="客户所在区域" name="district" initialValue="无">
          <Radio.Group buttonStyle="solid">{transferToDianShiFormOptions.districtWithEmpty.map(item => <Radio.Button key={item} value={item}>{item}</Radio.Button>)}</Radio.Group>
        </Form.Item>
        <Form.Item label="客户所在省市" name="area">
          <Cascader options={areaTree} />
        </Form.Item>
        <Form.Item label="客户所属行业" name="firstIncomeIndustryId" rules={[{ required: true, message: '不能为空' }]}>
          <CommonSelector {...industryList} valueKey="nodeCode" />
        </Form.Item>
        <Form.Item label="客户意向" name="intention" rules={[{ required: true, message: '不能为空' }]}>
          <CommonCascador {...leadIntentionLevel} />
        </Form.Item>
        <Form.Item label="关注产品" name="needProduct" rules={[{ required: true, message: '不能为空' }]}>
          <CommonCascador {...industryProductsOnlyValid} />
        </Form.Item>
        <Form.Item label="线索标签" name="leadTags" rules={[{ required: true, message: '不能为空' }]}>
          <CommonSelector mode="multiple" {...leadTags} />
        </Form.Item>
        <Form.Item label="意向采购时间" name="expectBuyDate">
          <DatePicker />
        </Form.Item>
        <Form.Item label="注册资本（人民币万元）" name="registeredCapital" rules={[{ required: true, message: '不能为空' }]}>
          <InputNumber min={0} max={99999999999999} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="公司规模" name="memberScale">
          <Select>{transferToDianShiFormOptions.memberScale.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
        </Form.Item>
        <Form.Item label="IT资金规模(月消耗)" name="monthConsumption">
          <Select>{transferToDianShiFormOptions.monthConsumption.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
        </Form.Item>
        <Form.Item label="联系人姓名" name="contactName" rules={[{ required: true, message: '不能为空' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="联系人职务" name="contactPosition" rules={[{ required: true, message: '不能为空' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="联系人电话" name="contactPhone" rules={[{ required: true, message: '不能为空' }, {
          validator: (rule, phoneInput) => {
          // 有可能编辑的时候信息已经是脱敏了，导致不符合正则规则，所以当只有修改之后才用正则校验
            if (!isEmptyWithCustom(phoneInput) && !phoneWithMaskPatten.test(phoneInput)) {
              return Promise.reject(new Error('电话号码格式不正确'))
            }
            return Promise.resolve()
          },
        }]}>
          <Input />
        </Form.Item>
        <Form.Item label="联系人身份" name="position" rules={[{ required: true, message: '不能为空' }]}>
          <Radio.Group buttonStyle="solid">{transferToDianShiFormOptions.position.map(item => <Radio.Button key={item} value={item}>{item}</Radio.Button>)}</Radio.Group>
        </Form.Item>
        <Form.Item label="客户需求" name="businessDemand" rules={[{ required: true, message: '不能为空' }]}>
          <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} allowClear placeholder="请输入" />
        </Form.Item>
        <Form.Item label="云厂商使用情况" name="cloudVendorUse">
          <Select mode="multiple" allowClear>{transferToDianShiFormOptions.cloudVendorUse.map(item => <Select.Option key={item} value={item}>{item}</Select.Option>)}</Select>
        </Form.Item>
        {!isEditMode
          ? (
            <Form.Item>
              <Space style={{ justifyContent: 'center', display: 'flex' }}>
                <Button type="primary" htmlType="submit">
                  提交
                </Button>
                <Button htmlType="reset" onClick={() => {
                  form.resetFields()
                  setCustomerInfo(null)
                }}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          )
          : (
            <div>
              <Space style={{ justifyContent: 'end', display: 'flex' }}>
                <Button htmlType="reset" onClick={() => {
                  form.resetFields()
                  setCustomerInfo(null)
                  onCancel && onCancel()
                }}>
                  取消
                </Button>
                <Button type="primary" onClick={onFinish}>
                  确定
                </Button>
              </Space>
            </div>
          )}
      </Form>
    </Spin>
  )
}
